import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ChangeMemberRoleResponse,
  MembershipOkResponse,
  OrganizationMembersResponse,
  OrgRole,
} from '@releve/shared';
import { authedDelete, authedGet, authedPatch } from '@/lib/api';
import { MEMBERSHIP_MUTATION_META, toastMembershipError } from '@/lib/membershipErrors';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';

export interface OrganizationMember {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  profiles?: {
    display_name: string | null;
    email: string | null;
  } | null;
}

// Query key factory
export const memberKeys = {
  all: ['organization-members'] as const,
  lists: () => [...memberKeys.all, 'list'] as const,
  list: (organizationId: string) => [...memberKeys.lists(), organizationId] as const,
};

/** `/api/v1/organizations/:orgId` — the membership routes (security package B). */
export const organizationApiPath = (organizationId: string) => `/api/v1/organizations/${encodeURIComponent(organizationId)}`;

/**
 * Fetch organization members with their profile data
 * (`GET /api/v1/organizations/:orgId/members`, the shape seven other consumers read).
 */
export function useOrganizationMembers(organizationId?: string) {
  return useQuery({
    queryKey: memberKeys.list(organizationId || ''),
    queryFn: async (): Promise<OrganizationMember[]> => {
      if (!organizationId) return [];
      const { members } = await authedGet<OrganizationMembersResponse>(`${organizationApiPath(organizationId)}/members`);
      return members;
    },
    enabled: !!organizationId,
  });
}

/**
 * Remove a member from the organization. The member log and the
 * `organization_members_audit` row are written by triggers in the database.
 */
export function useRemoveMember(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    meta: MEMBERSHIP_MUTATION_META,
    mutationFn: ({ memberId }: { memberId: string }) =>
      authedDelete<MembershipOkResponse>(`${organizationApiPath(organizationId)}/members/${encodeURIComponent(memberId)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.list(organizationId) });
      toast.success(i18n.t('team.toasts.memberRemoved'));
    },
    onError: (error: Error) => toastMembershipError(error, 'team.toasts.removeFailed'),
  });
}

/**
 * Change a member's role. The member log and the audit row are written by
 * triggers in the database.
 */
export function useChangeRole(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    meta: MEMBERSHIP_MUTATION_META,
    mutationFn: ({ memberId, newRole }: { memberId: string; newRole: string }) =>
      authedPatch<ChangeMemberRoleResponse>(`${organizationApiPath(organizationId)}/members/${encodeURIComponent(memberId)}`, {
        role: newRole as OrgRole,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.list(organizationId) });
      toast.success(i18n.t('team.toasts.roleUpdated'));
    },
    onError: (error: Error) => toastMembershipError(error, 'team.toasts.roleUpdateFailed'),
  });
}
